<script setup lang="ts">
import { useField } from 'vee-validate';
import { computed } from 'vue';
import { format, isValid, parse, parseISO } from 'date-fns';

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  name: string;
  label: string;
}>();

const { value: fieldValue, errorMessage } = useField<string | null>(props.name);

const STORAGE_TIME_FORMAT = 'HH:mm:ss';
const INPUT_TIME_FORMAT = 'HH:mm';

// Ties the QField label to the native input so clicking the label focuses it.
let instanceCount = 0;
const inputId = `base-time-input-${++instanceCount}`;

/**
 * Stored values come from several generations of this field: `HH:mm:ss` from the
 * API, `HH:mm` from older payloads and full ISO strings from before the time was
 * split out of the date. Accept all three so existing event items stay editable.
 */
function parseStoredTime(value: string | null): Date | null {
  if (typeof value !== 'string') return null;

  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  const timeWithSeconds = parse(trimmedValue, STORAGE_TIME_FORMAT, new Date());
  if (isValid(timeWithSeconds)) return timeWithSeconds;

  const timeWithoutSeconds = parse(trimmedValue, INPUT_TIME_FORMAT, new Date());
  if (isValid(timeWithoutSeconds)) return timeWithoutSeconds;

  const isoDateTime = parseISO(trimmedValue);
  return isValid(isoDateTime) ? isoDateTime : null;
}

const timeValue = computed<string>(() => {
  const parsedTime = parseStoredTime(fieldValue.value);
  return parsedTime ? format(parsedTime, INPUT_TIME_FORMAT) : '';
});

function handleInput(event: Event) {
  const { value } = event.target as HTMLInputElement;
  if (!value) {
    fieldValue.value = null;
    return;
  }

  // A native time input yields `HH:mm`, or `HH:mm:ss` when a sub-minute step is
  // set. Pad to the stored format either way rather than assuming the former.
  const [hours, minutes, seconds = '00'] = value.split(':');
  fieldValue.value = `${hours}:${minutes}:${seconds}`;
}
</script>

<template>
  <q-field
    outlined
    stack-label
    :for="inputId"
    :label="props.label"
    v-bind="$attrs"
    :error="!!errorMessage"
    :error-message="errorMessage"
  >
    <template v-slot:control>
      <input
        :id="inputId"
        type="time"
        class="q-field__native base-time-input__native"
        :value="timeValue"
        @input="handleInput"
      />
    </template>
  </q-field>
</template>

<style lang="scss">
.base-time-input__native {
  background: transparent;
  border: none;
  color: inherit;
  font: inherit;
  outline: none;
  padding: 0;
  width: 100%;
}
</style>
